<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card" style="margin-top: 50px">
        <Header :active-tab="activeTab" @open-add="onOpenAdd" />
        <div class="card-body">
          <div class="mb-5 w-75 d-flex gap-4 flex-column">
            <div class="row mb-2 align-items-center">
              <div class="col-sm-2 d-flex align-items-center">
                <label class="fw-semibold">IDP Period</label>
              </div>

              <div class="col-sm-10 d-flex align-items-center">
                <v-select
                  :options="idpPeriodOptions"
                  :reduce="(e) => e.value"
                  label="text"
                  v-model="filters.idp_period_id"
                  class="w-50"
                  placeholder="-- Select a period --"
                />
              </div>
            </div>
          </div>
          <Tabs :activeTab="activeTab" @tab-changed="onTabChange" :is-loading="isLoading" />
          <Summary :isLoadingSummary="isLoadingSummary" :summaryData="summaryIdpLearning" />

          <div class="row align-items-center justify-content-between mb-4">
            <div class="col-md-2">
              <h4 class="mb-0 font-weight-bold">Course List</h4>
            </div>
            <div class="col-md-3">
              <div class="d-flex justify-content-between align-items-center gap-2">
                <div class="w-100">
                  <b-form-input
                    class="w-full"
                    v-model="filters.search"
                    placeholder="Search Here..."
                    @keyup.enter="onSearch"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-outline-danger position-relative d-inline-flex align-items-center justify-content-center gap-1 px-3"
                    @click="showFilterModal"
                    style="min-height: 38px; line-height: 1;"
                  >
                    <span class="fe fe-sliders"></span> Filter
                    <span
                      v-if="activeFiltersCount > 0"
                      class="badge bg-danger position-absolute top-0 start-100 translate-middle rounded-pill"
                      style="font-size: 0.65rem; padding: 0.2rem 0.4rem"
                    >
                      {{ activeFiltersCount }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeFiltersCount > 0" class="mb-3">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <small class="text-muted">Active Filters:</small>

              <span v-if="filters.status" class="badge bg-light text-dark border">
                Status: {{ filters.status }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('status')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.approval_step" class="badge bg-light text-dark border">
                Approval: {{ approvalStepLabel }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('approval_step')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_scope" class="badge bg-light text-dark border">
                Training Scope: {{ filters.training_scope }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_scope')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_category" class="badge bg-light text-dark border">
                Course Type: {{ filters.training_category === 'mandatory' ? 'Mandatory' : 'Development' }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_category')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.coaching_type_id" class="badge bg-light text-dark border">
                Coaching Type: {{ filters.coaching_type_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('coaching_type_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.platform" class="badge bg-light text-dark border">
                Platform: {{ filters.platform }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('platform')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_method_id" class="badge bg-light text-dark border">
                Training Method ID: {{ filters.training_method_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_method_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.training_type" class="badge bg-light text-dark border">
                Training Type: {{ filters.training_type }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('training_type')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <span v-if="filters.competency_id" class="badge bg-light text-dark border">
                Competency: {{ filters.competency_id }}
                <button
                  type="button"
                  class="btn-close btn-close-sm ms-1"
                  @click="clearSingleFilter('competency_id')"
                  style="font-size: 0.6rem; padding: 0.1rem"
                ></button>
              </span>

              <button type="button" class="btn btn-sm btn-link text-danger p-0 ms-2" @click="resetFilters">Clear All</button>
            </div>
          </div>

          <CardGridStructured
            v-if="filters.learning_type === 'structured_learning'"
            :isLoading="isLoading"
            :items="idpLearnings.data"
            :page="idpLearnings.page"
            :page-size="idpLearnings.limit"
            :total-items="idpLearnings.totalItems"
            :total-pages="idpLearnings.totalPages"
            @page-change="onPageChange"
            @open-modal="openDetailModal"
          />

          <CardGridSocial
            v-if="filters.learning_type === 'social_learning'"
            :isLoading="isLoading"
            :items="idpLearnings.data"
            :page="idpLearnings.page"
            :page-size="idpLearnings.limit"
            :total-items="idpLearnings.totalItems"
            :total-pages="idpLearnings.totalPages"
            @page-change="onPageChange"
            @open-modal="openDetailModal"
            :selected-items="selectedItems"
            @select-change="onSelectChange"
            @open-confirm-submit-modal="showConfirmSubmitModal"
          />

          <CardGridExperiential
            v-if="filters.learning_type === 'experiential_learning'"
            :isLoading="isLoading"
            :items="idpLearnings.data"
            :page="idpLearnings.page"
            :page-size="idpLearnings.limit"
            :total-items="idpLearnings.totalItems"
            :total-pages="idpLearnings.totalPages"
            @page-change="onPageChange"
            @open-modal="openDetailModal"
          />

          <StructuredLearningModal
            v-if="filters.learning_type === 'structured_learning'"
            :modal-id="'structured-learning-modal'"
            :data="structuredLearningDetail"
            :is-loading="isModalLoading"
            @submit-enrollment="submitEnrollment"
            @show-terms-modal="showTermsModal"
            @close="showModal = false"
          />

          <SocialLearningModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'social-learning-modal'"
            :data="socialLearningDetail"
            :is-loading="isModalLoading"
            @close="showModal = false"
          />

          <ExperientialLearningModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'experiential-learning-modal'"
            :data="experientialLearningDetail"
            :is-loading="isModalLoading"
            @close="showModal = false"
            @open-update-activity-modal="showUpdateActivityModal"
            @open-delete-activity-confirm-modal="showDeleteActivityConfirmationModal"
            @open-add-activity-modal="showAddActivityModal"
            @open-submit-project-confirm-modal="showSubmitProjectConfirmationModal"
          />

          <!-- PDF-based Terms Modal -->
          <TermsConditionsModal
            v-if="useTabsTermsModal === false"
            :modal-id="'terms-conditions-modal'"
            :training-data="termsModalData.trainingData"
            :enrollment-payload="termsModalData.enrollmentPayload"
            @proceed-enrollment="proceedWithEnrollment"
            @modal-closed="resetTermsModal"
          />

          <!-- Tabs-based Terms Modal -->
          <TermsConditionsTabsModal
            v-if="useTabsTermsModal === true"
            :modal-id="'terms-conditions-tabs-modal'"
            :training-data="termsModalData.trainingData"
            :enrollment-payload="termsModalData.enrollmentPayload"
            @proceed-enrollment="proceedWithEnrollment"
            @modal-closed="resetTermsModal"
          />

          <modal-filter
            v-model="filters"
            @apply-filters="applyFilters"
            @reset-filters="resetFilters"
            :training-type-options="trainingTypes"
            :competency-options="competencies"
            :training-method-options="trainingMethods"
            :learning-type="filters.learning_type"
            :show-status-filter="true"
          />

          <ConfirmationSubmitModal
            :modal-id="'confirmation-submit-modal'"
            :selectedItems="selectedItems"
            :isSubmitting="isSubmitting"
            @submit="submitCoaching"
          />

          <AddCoachingModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'add-coaching-modal'"
            :competencyOptions="employeeCompetencies"
            :coachingOptions="listCoaching"
            :is-loading="isSubmitting"
            @fetch-coaching="getCoachingByCompetency"
            @submit="submitCoaching"
            @add-coaching="addCoaching"
          />

          <AddProjectModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'add-project-modal'"
            :competencyOptions="employeeCompetencies"
            :is-loading="isSubmitting"
            :employeeOptions="employees"
            @add-experiential-learning="addExperientialLearning"
          />

          <ProjectActivityModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'project-activity-modal'"
            :data="projectActivityData"
            :member-options="experientialLearningDetail?.project?.members"
            :id="experientialLearningDetail?.id"
            @submit-activity="submitProjectActivity"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import logic from "./main.logic";
import Header from "./components/header.vue";
import Summary from "./components/summary.vue";
import CardGridStructured from "./components/card-grid-structured.vue";
import StructuredLearningModal from "./modal/structured-learning-modal.vue";
import TermsConditionsModal from "./modal/terms-conditions-modal.vue";
import TermsConditionsTabsModal from "./modal/terms-conditions-tabs-modal.vue";
import ModalFilter from "./modal/filter-modal.vue";
import Tabs from "./components/tabs.vue";
import CardGridSocial from "./components/card-grid-social.vue";
import SocialLearningModal from "./modal/social-learning-modal.vue";
import ConfirmationSubmitModal from "./modal/confirmation-submit-modal.vue";
import AddCoachingModal from "./modal/add-coaching-modal.vue";
import CardGridExperiential from "./components/card-grid-experiential.vue";
import ExperientialLearningModal from "./modal/experiential-learning-modal.vue";
import ProjectActivityModal from "./modal/activity-modal.vue";
import AddProjectModal from "./modal/add-project-modal.vue";

export default {
  name: "IDPEnrollmentPage",
  components: {
    Header,
    CardGridStructured,
    Summary,
    StructuredLearningModal,
    TermsConditionsModal,
    TermsConditionsTabsModal,
    ModalFilter,
    CardGridSocial,
    SocialLearningModal,
    ConfirmationSubmitModal,
    CardGridExperiential,
    AddCoachingModal,
    Tabs,
    ExperientialLearningModal,
    ProjectActivityModal,
    AddProjectModal,
  },
  mixins: [logic],
};
</script>
